import React from 'react';
import {
    Award,
    ShieldCheck,
    Microscope,
    Zap,
    ChevronRight,
    Home,
    Star,
    GraduationCap,
    MapPin,
    Calendar
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// --- Types & Interfaces ---
interface Certification {
    title: string;
    organization: string;
    accreditation?: string;
    location?: string;
    date: string;
    icon: React.ReactNode;
}

const Certifications: React.FC = () => {
    const certifications: Certification[] = [
        {
            title: "Retrograde Intrarenal Surgery (RIRS)",
            organization: "Institute of Medical and Minimal Access Surgery Training (IMMAST)",
            accreditation: "Awarded up to 12 CPD points by The Royal College of Surgeons of England",
            date: "October 19th - 20th, 2023",
            icon: <Zap className="w-6 h-6 text-blue-600" />
        },
        {
            title: "Microsurgery Training Workshop",
            organization: "Androcon 2023 | Urological Society of India",
            location: "Hotel Sheraton ITC, Saket, New Delhi",
            date: "July 14th - 16th, 2023",
            icon: <Microscope className="w-6 h-6 text-teal-600" />
        },
        {
            title: "Andrology Course (Male Infertility & Sexual Health)",
            organization: "Department of Urology, Muljibhai Patel Urological Hospital, Nadiad",
            accreditation: "Gujarat Medical Council (12 Credit Points)",
            date: "May 18th - 21st, 2023",
            icon: <ShieldCheck className="w-6 h-6 text-indigo-600" />
        },
        {
            title: "LASERCON 2022: Mid-Term Live LASER Workshop",
            organization: "Haryana Urological Association (Under the aegis of North Zone Urological Society of India)",
            location: "Sarvodaya Hospital, Faridabad",
            date: "June 25th - 26th, 2022",
            icon: <Award className="w-6 h-6 text-orange-600" />
        }
    ];

    const recognitions = [
        {
            title: "Faculty – GBH National Oncology Conference",
            venue: "AIIMS Medical College Auditorium",
            organization: "GBH General & Memorial Cancer Hospital",
            location: "Udaipur, Rajasthan",
            date: "September 28–29, 2024",
        }
    ];


    return (
        <div className="pt-20 bg-[#f8fafc] min-h-screen font-sans">
            {/* --- HERO SECTION --- */}
            <section className="bg-slate-900 py-20 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-500 via-transparent to-transparent" />
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-slate-400 text-sm font-bold uppercase tracking-widest mb-6">
                        <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
                            <Home className="w-4 h-4" /> Home
                        </Link>
                        <ChevronRight className="w-4 h-4 opacity-50" />
                        <span className="text-blue-400">Certifications</span>
                    </nav>
                    <h1 className="text-4xl md:text-6xl font-black text-white tracking-tight italic">
                        Professional Certifications
                    </h1>
                    <p className="text-slate-400 mt-4 max-w-xl font-medium">
                        Documented advanced surgical training and continuous medical education in modern urology.
                    </p>
                </div>
            </section>

            {/* --- TRAINING CARDS --- */}
            <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
                    <div>
                        <div className="flex items-center gap-3 text-blue-600 mb-2">
                            <GraduationCap className="w-6 h-6" />
                            <span className="font-black uppercase tracking-tighter">Continuing Medical Education</span>
                        </div>
                        <h2 className="text-4xl font-black text-slate-900">Certifications & Clinical Training</h2>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    {certifications.map((cert, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            viewport={{ once: true }}
                            className="group bg-white rounded-[2.5rem] p-8 md:p-10 border border-slate-200 shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 relative overflow-hidden"
                        >
                            {/* Decorative Background Icon */}
                            <div className="absolute -right-4 -bottom-4 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity">
                                {React.cloneElement(cert.icon as React.ReactElement, { className: "w-40 h-40" } as any)}
                            </div>

                            <div className="relative z-10">
                                <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-8 border border-slate-100 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-500">
                                    {React.cloneElement(cert.icon as React.ReactElement, {
                                        className: "w-8 h-8 group-hover:text-white"
                                    } as any)}
                                </div>

                                <h3 className="text-2xl font-black text-slate-900 mb-3 leading-tight group-hover:text-blue-700 transition-colors">
                                    {cert.title}
                                </h3>
                                <p className="text-slate-600 font-bold mb-6 italic leading-relaxed">
                                    {cert.organization}
                                </p>

                                <div className="space-y-3">
                                    {cert.accreditation && (
                                        <div className="flex items-center gap-3 bg-blue-50/50 p-3 rounded-xl border border-blue-100/50">
                                            <ShieldCheck className="w-4 h-4 text-blue-600 shrink-0" />
                                            <span className="text-blue-900 text-xs font-black uppercase tracking-wide">
                                                {cert.accreditation}
                                            </span>
                                        </div>
                                    )}

                                    <div className="flex flex-wrap gap-4 pt-2">
                                        <div className="flex items-center gap-2 text-slate-400 text-xs font-bold">
                                            <Calendar className="w-4 h-4" /> {cert.date}
                                        </div>
                                        {cert.location && (
                                            <div className="flex items-center gap-2 text-slate-400 text-xs font-bold">
                                                <MapPin className="w-4 h-4" /> {cert.location}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* --- AWARDS SECTION --- */}
            <section className="py-24 bg-white border-y border-slate-100">
                <div className="max-w-5xl mx-auto px-4">
                    <div className="bg-slate-900 rounded-[3rem] p-10 md:p-20 relative overflow-hidden shadow-2xl">
                        <div className="absolute top-0 right-0 p-12 opacity-10">
                            <Star className="w-40 h-40 text-yellow-500" />
                        </div>

                        <h2 className="text-3xl md:text-4xl font-black text-white italic mb-12 relative z-10">
                            Academic Recognition
                        </h2>

                        <div className="grid gap-6 relative z-10">
                            {recognitions.map((item, i) => (
                                <div key={i} className="flex items-center gap-6 bg-white/5 p-6 rounded-2xl border border-white/10">

                                    <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
                                        <Award className="w-6 h-6 text-yellow-500" />
                                    </div>

                                    <div>
                                        <p className="text-lg font-bold text-white">
                                            {item.title}
                                        </p>
                                        <p className="text-sm text-slate-300">
                                            {item.organization}, {item.venue}
                                        </p>
                                        <p className="text-xs text-slate-400">
                                            {item.location} • {item.date}
                                        </p>
                                    </div>

                                </div>
                            ))}

                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Certifications;